const LEGACY_INSTANCE_PREFIX = 'legacy-inst'
const LIVE_INSTANCE_PREFIX = 'inst'
const RESERVED_CATEGORY_KEYS = new Set(['gravestones'])

const normalizeId = (value) => String(value || '').trim()

export const normalizeSceneInstanceCategory = (value) => {
    if (Array.isArray(value)) return value.map(normalizeId).filter(Boolean)
    const single = normalizeId(value)
    return single ? [single] : []
}

export const getSceneInstanceCategoryKeys = (selectedModels = {}) => {
    const keys = []

    if (normalizeId(selectedModels?.karkas)) keys.push('karkas')
    if (normalizeId(selectedModels?.monumentLeft)) keys.push('monumentLeft')

    Object.keys(selectedModels || {})
        .filter((key) => key !== 'karkas' && key !== 'monumentLeft' && !RESERVED_CATEGORY_KEYS.has(key))
        .sort()
        .forEach((key) => {
            if (normalizeSceneInstanceCategory(selectedModels?.[key]).length) keys.push(key)
        })

    return keys
}

const safeToken = (value) => encodeURIComponent(normalizeId(value) || 'unknown')

export const createSceneInstanceSeed = () => {
    const randomPart = (() => {
        try {
            if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
                return window.crypto.randomUUID().replace(/-/g, '')
            }
        } catch (_error) {
            // Падаем в компактный fallback ниже.
        }

        return Math.random().toString(36).slice(2, 12)
    })()

    return `${Date.now().toString(36)}-${randomPart}`
}

export const createLegacySceneInstanceId = (categoryKey, index, productId) => (
    `${LEGACY_INSTANCE_PREFIX}:${safeToken(categoryKey)}:${Math.max(0, Number(index) || 0)}:${safeToken(productId)}`
)

export const createSceneInstanceId = (categoryKey, index, productId, seed = '') => (
    `${LIVE_INSTANCE_PREFIX}:${safeToken(seed || createSceneInstanceSeed())}:${safeToken(categoryKey)}:${Math.max(0, Number(index) || 0)}:${safeToken(productId)}`
)

const normalizeExistingIds = (value) => (
    Array.isArray(value)
        ? value.map(normalizeId)
        : (normalizeId(value) ? [normalizeId(value)] : [])
)

export const ensureSceneInstanceIds = (
    selectedModels = {},
    instanceIds = {},
    { fallbackSeed = 'legacy' } = {}
) => {
    const result = {}
    const used = new Set()
    const repairSeed = createSceneInstanceSeed()

    getSceneInstanceCategoryKeys(selectedModels).forEach((categoryKey) => {
        const products = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
        const existing = normalizeExistingIds(instanceIds?.[categoryKey])

        result[categoryKey] = products.map((productId, index) => {
            const candidate = normalizeId(existing[index])

            if (candidate && !used.has(candidate)) {
                used.add(candidate)
                return candidate
            }

            const generated = fallbackSeed && fallbackSeed !== 'legacy'
                ? createSceneInstanceId(
                    categoryKey,
                    index,
                    productId,
                    `${fallbackSeed}:${repairSeed}:${index}`
                )
                : createLegacySceneInstanceId(categoryKey, index, productId)

            if (!used.has(generated)) {
                used.add(generated)
                return generated
            }

            const repaired = createSceneInstanceId(
                categoryKey,
                index,
                productId,
                `${repairSeed}:dedupe:${index}`
            )
            used.add(repaired)
            return repaired
        })
    })

    return result
}

export const reconcileSceneInstanceIds = (
    previousSelectedModels = {},
    nextSelectedModels = {},
    previousInstanceIds = {},
    { seed = '' } = {}
) => {
    const normalizedPrevious = ensureSceneInstanceIds(previousSelectedModels, previousInstanceIds)
    const result = {}
    const newSeed = seed || createSceneInstanceSeed()
    const used = new Set()

    getSceneInstanceCategoryKeys(nextSelectedModels).forEach((categoryKey) => {
        const previousProducts = normalizeSceneInstanceCategory(previousSelectedModels?.[categoryKey])
        const nextProducts = normalizeSceneInstanceCategory(nextSelectedModels?.[categoryKey])
        const previousIds = normalizeExistingIds(normalizedPrevious?.[categoryKey])
        const previousCountByProduct = new Map()
        const nextCountByProduct = new Map()

        previousProducts.forEach((productId) => {
            previousCountByProduct.set(productId, (previousCountByProduct.get(productId) || 0) + 1)
        })
        nextProducts.forEach((productId) => {
            nextCountByProduct.set(productId, (nextCountByProduct.get(productId) || 0) + 1)
        })

        const categoryUnchanged = previousProducts.length === nextProducts.length &&
            previousProducts.every((productId, index) => productId === nextProducts[index])
        const isPureAppend = nextProducts.length >= previousProducts.length &&
            previousProducts.every((productId, index) => productId === nextProducts[index])
        result[categoryKey] = nextProducts.map((productId, index) => {
            let candidate = ''

            // Полностью неизменённый массив — та же физическая сцена, IDs остаются на местах.
            if (categoryUnchanged || (isPureAppend && index < previousProducts.length)) {
                candidate = normalizeId(previousIds[index])
            } else {
                const previousCount = previousCountByProduct.get(productId) || 0
                const nextCount = nextCountByProduct.get(productId) || 0

                // Для уникального товара переносим его физическую identity даже после reorder.
                if (previousCount === 1 && nextCount === 1) {
                    const previousIndex = previousProducts.indexOf(productId)
                    candidate = normalizeId(previousIds[previousIndex])
                } else if (
                    previousCount > 0 &&
                    nextCount > 0 &&
                    previousProducts[index] === productId &&
                    previousCount === nextCount
                ) {
                    // Дубликаты можно сохранить позиционно только если их количество не менялось.
                    // При insert/remove одинаковых товаров нельзя угадывать, какой экземпляр выжил.
                    candidate = normalizeId(previousIds[index])
                }
            }

            if (candidate && !used.has(candidate)) {
                used.add(candidate)
                return candidate
            }

            const generated = createSceneInstanceId(
                categoryKey,
                index,
                productId,
                `${newSeed}:${index}`
            )
            used.add(generated)
            return generated
        })
    })

    return result
}

export const hydrateSceneInstanceIds = ({
    currentSelectedModels = {},
    currentInstanceIds = {},
    nextSelectedModels = {},
    snapshotInstanceIds = {},
    preserveCurrent = false,
    contextSeed = 'snapshot',
} = {}) => {
    const currentNormalized = ensureSceneInstanceIds(currentSelectedModels, currentInstanceIds)
    const snapshotNormalized = snapshotInstanceIds && typeof snapshotInstanceIds === 'object'
        ? snapshotInstanceIds
        : {}
    const result = {}
    const used = new Set()
    const repairSeed = createSceneInstanceSeed()

    getSceneInstanceCategoryKeys(nextSelectedModels).forEach((categoryKey) => {
        const nextProducts = normalizeSceneInstanceCategory(nextSelectedModels?.[categoryKey])
        const currentProducts = normalizeSceneInstanceCategory(currentSelectedModels?.[categoryKey])
        const currentIds = normalizeExistingIds(currentNormalized?.[categoryKey])
        const incomingIds = normalizeExistingIds(snapshotNormalized?.[categoryKey])

        result[categoryKey] = nextProducts.map((productId, index) => {
            const candidates = [
                incomingIds[index],
                (
                    preserveCurrent &&
                    currentProducts[index] === productId
                        ? currentIds[index]
                        : ''
                ),
            ]

            for (const candidateRaw of candidates) {
                const candidate = normalizeId(candidateRaw)

                if (candidate && !used.has(candidate)) {
                    used.add(candidate)
                    return candidate
                }
            }

            const legacyId = createLegacySceneInstanceId(
                `${contextSeed || 'snapshot'}:${categoryKey}`,
                index,
                productId
            )

            if (!used.has(legacyId)) {
                used.add(legacyId)
                return legacyId
            }

            const repaired = createSceneInstanceId(
                categoryKey,
                index,
                productId,
                `${contextSeed || 'snapshot'}:${repairSeed}:dedupe:${index}`
            )
            used.add(repaired)
            return repaired
        })
    })

    return result
}

export const removeSceneInstanceIds = (
    selectedModels = {},
    instanceIds = {},
    removeIndexesByCategory = {}
) => {
    const normalized = ensureSceneInstanceIds(selectedModels, instanceIds)
    const result = {}

    getSceneInstanceCategoryKeys(selectedModels).forEach((categoryKey) => {
        const products = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
        const ids = normalizeExistingIds(normalized?.[categoryKey])
        const removeSet = removeIndexesByCategory?.[categoryKey] instanceof Set
            ? removeIndexesByCategory[categoryKey]
            : new Set(Array.isArray(removeIndexesByCategory?.[categoryKey])
                ? removeIndexesByCategory[categoryKey]
                : [])

        const kept = ids.filter((_id, index) => index < products.length && !removeSet.has(index))
        if (kept.length) result[categoryKey] = kept
    })

    return result
}

export const appendSceneInstanceIds = (
    selectedModels = {},
    instanceIds = {},
    categoryKey,
    productId,
    count = 1,
    seed = ''
) => {
    const normalized = ensureSceneInstanceIds(selectedModels, instanceIds)
    const currentProducts = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
    const currentIds = normalizeExistingIds(normalized?.[categoryKey])
    const addCount = Math.max(0, Math.round(Number(count) || 0))
    const newSeed = seed || createSceneInstanceSeed()
    const next = [...currentIds]

    for (let offset = 0; offset < addCount; offset += 1) {
        const index = currentProducts.length + offset
        next.push(createSceneInstanceId(categoryKey, index, productId, `${newSeed}:${offset}`))
    }

    return {
        ...normalized,
        [categoryKey]: next,
    }
}

export const getSceneInstanceIdAt = (
    selectedModels = {},
    instanceIds = {},
    categoryKey,
    index = 0
) => {
    const products = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
    if (!products[index]) return ''

    const normalized = ensureSceneInstanceIds(selectedModels, instanceIds)
    return normalizeId(normalized?.[categoryKey]?.[index])
}

// Stable copy paths are stored by instanceId, never by array position.
// Example: '' (original), '__1', '__2', '__1__1'.
const COPY_PATH_SEPARATOR = '__'

export const getSceneInstancePath = (productId, copyPath = '') => (
    `${normalizeId(productId)}${String(copyPath || '')}`
)

const collectLiveSceneInstanceIds = (instanceIds = {}) => {
    const live = new Set()

    Object.values(instanceIds || {}).forEach((value) => {
        normalizeExistingIds(value).forEach((instanceId) => {
            if (instanceId) live.add(instanceId)
        })
    })

    return live
}

export const allocateSceneCopySuffixes = (
    usedSuffixes = new Set(),
    parentSuffix = '',
    count = 1
) => {
    const used = usedSuffixes instanceof Set ? usedSuffixes : new Set(usedSuffixes || [])
    const parent = String(parentSuffix || '')
    const result = []
    const requested = Math.max(0, Math.round(Number(count) || 0))
    let ordinal = 1

    while (result.length < requested) {
        const candidate = `${parent}${COPY_PATH_SEPARATOR}${ordinal}`
        ordinal += 1

        if (used.has(candidate)) continue

        used.add(candidate)
        result.push(candidate)
    }

    return result
}

export const ensureSceneCopyPaths = (
    selectedModels = {},
    instanceIds = {},
    copyPaths = {}
) => {
    const normalizedInstanceIds = ensureSceneInstanceIds(selectedModels, instanceIds)
    const incoming = (
        copyPaths && typeof copyPaths === 'object' && !Array.isArray(copyPaths)
            ? copyPaths
            : {}
    )
    const next = {}
    const usedByProduct = new Map()
    const pendingByProduct = new Map()

    getSceneInstanceCategoryKeys(selectedModels).forEach((categoryKey) => {
        const products = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
        const ids = normalizeExistingIds(normalizedInstanceIds?.[categoryKey])

        products.forEach((productId, index) => {
            const instanceId = normalizeId(ids[index])
            if (!instanceId) return

            const productKey = normalizeId(productId)
            if (!usedByProduct.has(productKey)) usedByProduct.set(productKey, new Set())
            if (!pendingByProduct.has(productKey)) pendingByProduct.set(productKey, [])

            const used = usedByProduct.get(productKey)
            const requested = typeof incoming[instanceId] === 'string'
                ? incoming[instanceId]
                : null

            // Существующий suffix сохраняем только если он не конфликтует с другим
            // физическим экземпляром того же товара. Второй конфликтующий экземпляр
            // получает новый свободный suffix ниже.
            if (requested !== null && !used.has(requested)) {
                used.add(requested)
                next[instanceId] = requested
                return
            }

            pendingByProduct.get(productKey).push({ instanceId, index })
        })
    })

    pendingByProduct.forEach((pending, productKey) => {
        const used = usedByProduct.get(productKey) || new Set()

        pending
            .sort((a, b) => a.index - b.index)
            .forEach(({ instanceId }) => {
                let copyPath = ''

                if (used.has(copyPath)) {
                    let ordinal = 1
                    copyPath = `${COPY_PATH_SEPARATOR}${ordinal}`

                    while (used.has(copyPath)) {
                        ordinal += 1
                        copyPath = `${COPY_PATH_SEPARATOR}${ordinal}`
                    }
                }

                used.add(copyPath)
                next[instanceId] = copyPath
            })
    })

    return next
}

export const validateSceneInstanceIdentity = (
    selectedModels = {},
    instanceIds = {},
    copyPaths = {}
) => {
    const normalizedIds = ensureSceneInstanceIds(selectedModels, instanceIds)
    const normalizedCopyPaths = ensureSceneCopyPaths(selectedModels, normalizedIds, copyPaths)
    const records = buildSceneInstanceRecords(selectedModels, normalizedIds, normalizedCopyPaths)
    const seenIds = new Set()
    const seenPaths = new Set()
    const duplicateInstanceIds = []
    const duplicatePaths = []

    records.forEach((record) => {
        if (seenIds.has(record.instanceId)) duplicateInstanceIds.push(record.instanceId)
        else seenIds.add(record.instanceId)

        if (seenPaths.has(record.path)) duplicatePaths.push(record.path)
        else seenPaths.add(record.path)
    })

    return {
        valid: duplicateInstanceIds.length === 0 && duplicatePaths.length === 0,
        duplicateInstanceIds: [...new Set(duplicateInstanceIds)],
        duplicatePaths: [...new Set(duplicatePaths)],
        instanceIds: normalizedIds,
        copyPaths: normalizedCopyPaths,
        records,
    }
}

export const hydrateSceneCopyPaths = ({
    selectedModels = {},
    instanceIds = {},
    snapshotCopyPaths = {},
    currentCopyPaths = {},
    preserveCurrent = false,
} = {}) => {
    const incoming = (
        snapshotCopyPaths &&
        typeof snapshotCopyPaths === 'object' &&
        !Array.isArray(snapshotCopyPaths)
    )
        ? snapshotCopyPaths
        : {}
    const current = (
        currentCopyPaths &&
        typeof currentCopyPaths === 'object' &&
        !Array.isArray(currentCopyPaths)
    )
        ? currentCopyPaths
        : {}

    const base = preserveCurrent
        ? { ...current, ...incoming }
        : { ...incoming }

    return ensureSceneCopyPaths(selectedModels, instanceIds, base)
}

export const buildSceneInstanceRecords = (
    selectedModels = {},
    instanceIds = {},
    copyPaths = {}
) => {
    const normalizedInstanceIds = ensureSceneInstanceIds(selectedModels, instanceIds)
    const normalizedCopyPaths = ensureSceneCopyPaths(
        selectedModels,
        normalizedInstanceIds,
        copyPaths
    )
    const records = []

    getSceneInstanceCategoryKeys(selectedModels).forEach((categoryKey) => {
        const products = normalizeSceneInstanceCategory(selectedModels?.[categoryKey])
        const ids = normalizeExistingIds(normalizedInstanceIds?.[categoryKey])

        products.forEach((productId, index) => {
            const instanceId = normalizeId(ids[index])
            if (!instanceId) return

            const normalizedProductId = normalizeId(productId)
            const copyPath = String(normalizedCopyPaths[instanceId] || '')
            const path = getSceneInstancePath(normalizedProductId, copyPath)

            records.push({
                sourceType: 'standard',
                categoryKey,
                index,
                productId: normalizedProductId,
                baseId: normalizedProductId,
                rawId: normalizedProductId,
                instanceId,
                copyPath,
                suffix: copyPath,
                path,
            })
        })
    })

    return records
}

export const getSceneInstanceModelPaths = (
    selectedModels = {},
    instanceIds = {},
    copyPaths = {}
) => (
    buildSceneInstanceRecords(selectedModels, instanceIds, copyPaths)
        .map((record) => record.path)
)
